import format from 'date-fns/format'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import { ENTERPRISE_CUSTOMER_ID_PREFIX } from '@/utils/constants'
import { isBrandedHost } from '@/composables/useBrand'

const LIVE_PRICES = {
  basic: { monthly: 'price_1RD5yXALrMdFlaXPltMstvIv', yearly: 'price_1UQ0ATALrMdFlaXPclMGIzjO', previousYearly: 'price_1RD5yXALrMdFlaXPQ2vuB0Eo' },
  pro: { monthly: 'price_1RD5yRALrMdFlaXP5IaW68OL', yearly: 'price_1UQ0AUALrMdFlaXPrFnxATiH', previousYearly: 'price_1RD5yRALrMdFlaXPSaRp1XjS' }
}

const TEST_PRICES = {
  basic: { monthly: 'price_1QRUjHALrMdFlaXPttQI8y0K', yearly: 'price_1UQ007ALrMdFlaXP69ZtWqXw', previousYearly: 'price_1QRUjHALrMdFlaXPKdK19nDM' },
  pro: { monthly: 'price_1QRUkfALrMdFlaXPmR5WZ9G9', yearly: 'price_1UQ008ALrMdFlaXP5ybMQsu8', previousYearly: 'price_1QRUkfALrMdFlaXPOT0g83yb' }
}

const priceSet = isBrandedHost ? LIVE_PRICES : TEST_PRICES

// Temporary: sends an eligible new signup straight into the trial instead of via /checkout.
// Set to false to put the checkout step back.
export const SKIP_TRIAL_CHECKOUT = true

export const PLANS = [
  {
    name: 'Basic',
    value: 'basic',
    monthlyPrice: 25,
    yearlyPrice: 252,
    maxProjectsCount: 3,
    description: 'For individual users or small teams.',
    prices: {
      monthly: priceSet.basic.monthly,
      yearly: priceSet.basic.yearly,
      previousYearly: priceSet.basic.previousYearly
    },
    features: [
      '3 projects, 5 workflows in each',
      '10 GB for documents and attachments',
      'Unlimited AI prompts, with no credits to buy',
      'Unlimited viewers, with no sign-in and no seat',
      'MCP integration with Claude Code, Cursor, VS Code and Lovable',
      'Jira, Azure DevOps, GitHub and file exports'
    ]
  },
  {
    name: 'Pro',
    value: 'pro',
    monthlyPrice: 40,
    yearlyPrice: 396,
    maxProjectsCount: Infinity,
    description: 'For teams running modeling continuously.',
    prices: {
      monthly: priceSet.pro.monthly,
      yearly: priceSet.pro.yearly,
      previousYearly: priceSet.pro.previousYearly
    },
    features: [
      'Unlimited projects and workflows',
      'Unlimited document storage',
      'Onboarding and training session for the team',
      'Priority email support',
      '99.9% uptime target'
    ]
  }
]

export const ENTERPRISE_PLAN = {
  name: 'Enterprise',
  value: 'enterprise',
  maxProjectsCount: Infinity,
  description: 'For regulated and large organizations.',
  features: [
    'SAML SSO with any identity provider',
    'Choose your data residency',
    'Bring your own AI model',
    'Invoicing, bank transfer and annual contracts',
    'Dedicated account manager, phone and video support',
    'Custom integrations and workshops'
  ]
}

export function getPlanByPriceId (priceId) {
  return PLANS.find((plan) => [plan.prices.monthly, plan.prices.yearly, plan.prices.previousYearly].includes(priceId))
}

export function getSubscriptionTypeByPriceId (priceId) {
  const isMonthly = PLANS.findIndex((plan) => [plan.prices.monthly].includes(priceId)) !== -1
  const isYearly = PLANS.findIndex((plan) => [plan.prices.yearly, plan.prices.previousYearly].includes(priceId)) !== -1

  return isMonthly ? 'monthly' : isYearly ? 'yearly' : null
}

export const getPlanPrice = (plan, subscriptionType) => {
  return subscriptionType === 'yearly' ? plan?.yearlyPrice : plan?.monthlyPrice
}

export const getOrderCalculation = ({ plan, subscriptionType, usersCount }) => {
  const users = `${usersCount} ${usersCount === 1 ? 'user' : 'users'}`

  return subscriptionType === 'yearly'
    ? `US$${plan?.yearlyPrice / 12} x 12 months x ${users}`
    : `US$${plan?.monthlyPrice} x 1 month x ${users}`
}

export const getCurrentPricePerUser = (activeSubscription, plan, subscriptionType) => {
  const price = activeSubscription?.subscriptionDetails?.price || activeSubscription?.lastSubscriptionDetails?.price

  return price?.unitAmount ?? getPlanPrice(plan, subscriptionType)
}

export function getSubscriptionAccessType (activeSubscription) {
  if (!activeSubscription || activeSubscription.subscriptionDetails?.status === 'unpaid') {
    return 'NO_ACCESS'
  }

  const isEnterpriseUser = !!activeSubscription &&
    activeSubscription?.subscriptionDetails?.subscriptionId?.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX)
  const isSubscriptionActive = ['active', 'trialing'].includes(activeSubscription?.subscriptionDetails?.status)
  const lastSubscriptionDetails = activeSubscription?.lastSubscriptionDetails

  return isSubscriptionActive ? 'FULL' : isEnterpriseUser || lastSubscriptionDetails ? 'LIMITED' : 'NO_ACCESS'
}

export const SUBSCRIPTION_STATE = {
  ACTIVE: 'ACTIVE',
  TRIALING: 'TRIALING',
  PAST_DUE: 'PAST_DUE',
  UNPAID: 'UNPAID',
  INCOMPLETE: 'INCOMPLETE',
  CANCELED: 'CANCELED',
  NONE: 'NONE'
}

export function getSubscriptionState (activeSubscription) {
  if (!activeSubscription) {
    return SUBSCRIPTION_STATE.NONE
  }

  const status = activeSubscription.subscriptionDetails?.status ||
    activeSubscription.lastSubscriptionDetails?.status

  switch (status) {
    case 'active':
      return SUBSCRIPTION_STATE.ACTIVE
    case 'trialing':
      return SUBSCRIPTION_STATE.TRIALING
    case 'past_due':
      return SUBSCRIPTION_STATE.PAST_DUE
    case 'unpaid':
      return SUBSCRIPTION_STATE.UNPAID
    case 'incomplete':
      return SUBSCRIPTION_STATE.INCOMPLETE
    case 'canceled':
    case 'incomplete_expired':
      return SUBSCRIPTION_STATE.CANCELED
    default:
      return SUBSCRIPTION_STATE.NONE
  }
}

export function isRecoverableState (subscriptionState) {
  return [
    SUBSCRIPTION_STATE.PAST_DUE,
    SUBSCRIPTION_STATE.UNPAID,
    SUBSCRIPTION_STATE.INCOMPLETE
  ].includes(subscriptionState)
}

export function getScheduledQuantity (activeSubscription) {
  return activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.quantity
}

export function hasCardOnFile (activeSubscription) {
  const lastFour = activeSubscription?.subscriptionDetails?.cardDetails?.lastFour ||
    activeSubscription?.lastSubscriptionDetails?.cardDetails?.lastFour

  return !!lastFour && lastFour !== 'N/A'
}

export const getEntitledPlan = (activeSubscription) => {
  if (!activeSubscription) {
    return null
  }

  const { subscriptionId, status, price } = activeSubscription.subscriptionDetails || {}

  if (subscriptionId?.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX)) {
    return ENTERPRISE_PLAN
  }

  // Every feature stays open until the trial ends, even when Basic was chosen during it.
  if (status === 'trialing') {
    return PLANS.find((plan) => plan.value === 'pro')
  }

  return getPlanByPriceId(price?.id)
}

export const getTrialEndDate = (activeSubscription) => {
  const { status, nextBillingDate } = activeSubscription?.subscriptionDetails || {}

  return status === 'trialing' && nextBillingDate ? format(new Date(nextBillingDate), 'MMMM d, yyyy') : null
}

export const getTrialDaysLeft = (activeSubscription) => {
  const { status, nextBillingDate } = activeSubscription?.subscriptionDetails || {}

  return status === 'trialing' && nextBillingDate ? differenceInCalendarDays(new Date(nextBillingDate), new Date()) : null
}

// Choosing a plan clears the trial's cancel date and needs a card; trials started before that rule have neither.
export const hasUnchosenTrial = (activeSubscription) => {
  const { status, cancelAt } = activeSubscription?.subscriptionDetails || {}

  return status === 'trialing' && (!!cancelAt || !hasCardOnFile(activeSubscription))
}

// A trial that ended without a plan only ever had $0 invoices; a former paying customer's last invoice was not.
export const hasEndedUnchosenTrial = (activeSubscription) => {
  const last = activeSubscription?.lastSubscriptionDetails

  return !activeSubscription?.subscriptionDetails && last?.status === 'canceled' && last.latestInvoice?.amountDue === 0
}

// The button label on the plan card and the checkout page's whole card-or-no-card mode must
// answer this identically, or the user is offered a trial the backend will refuse.
// A currentUser that has not loaded yet stays eligible on purpose: it is the ordinary first paint
// for a new signup, and denying it would flash "Choose" on the card before flipping back.
// Callers that act on the answer rather than render it guard for the missing user themselves.
export const canStartCardlessTrial = ({ plan, activeSubscription, currentUser }) => {
  const isMemberStartingOwnSubscription = !!activeSubscription?.payerUserId &&
    activeSubscription.payerUserId !== currentUser?.id

  return !!plan?.prices &&
    !activeSubscription?.subscriptionDetails &&
    !activeSubscription?.lastSubscriptionDetails &&
    !isMemberStartingOwnSubscription
}
